<template>
  <section
    id="product-guided-checkout"
    class="o-product-guided-checkout"
  >
    <MProductOptionsBundleRz
      ref="bundleOptions"
      :product="product"
      :suppress-scroll="true"
      class="o-product-guided-checkout__bundle-engine"
    />

    <div class="o-product-guided-checkout__steps">
      <MGcStepPackage
        v-if="isStepVisible('package')"
        v-model="selectedPreset"
        :product="product"
        :bundle-options-component="bundleOptionsComponent"
        @changed="refreshSummary"
      />

      <hr
        v-if="isStepVisible('size')"
        class="divider divider-sm"
      >

      <MGcStepSize
        v-if="isStepVisible('size')"
        v-model="mainQuantity"
        :product="product"
        :bundle-options-component="bundleOptionsComponent"
        @changed="refreshSummary"
      />

      <hr
        v-if="isStepVisible('skirt')"
        class="divider divider-sm"
      >

      <MGcStepSkirt
        v-if="isStepVisible('skirt')"
        :step-number="stepNumber('skirt')"
        :product="product"
        :package-preset="selectedPreset"
        :bundle-options-component="bundleOptionsComponent"
        @changed="refreshSummary"
      />

      <hr
        v-if="isStepVisible('underlay') && underlayOption"
        class="divider divider-sm"
      >

      <MGcStepUnderlay
        v-if="isStepVisible('underlay') && underlayOption"
        :step-number="stepNumber('underlay')"
        :product="product"
        :package-preset="selectedPreset"
        :bundle-options-component="bundleOptionsComponent"
        @changed="refreshSummary"
      />

      <hr
        v-if="isStepVisible('accessories')"
        class="divider divider-sm"
      >

      <MGcStepAccessories
        v-if="isStepVisible('accessories')"
        v-model="selectedAccessories"
        :step-number="stepNumber('accessories')"
        :product="product"
        :selected-skirt-product="selectedSkirtProduct"
        @changed="refreshSummary"
      />

      <hr
        v-if="isStepVisible('fulfillment')"
        class="divider divider-sm"
      >

      <MGcStepFulfillment
        v-if="isStepVisible('fulfillment')"
        v-model="fulfillmentMode"
        :step-number="stepNumber('fulfillment')"
        :product="product"
        :product-stock="productStock"
      />
    </div>

    <MGuidedCheckoutBar
      :product="product"
      :product-name="product.name"
      :area-label="summaryAreaLabel"
      :total-price="totalPrice"
      :price-lines="priceLines"
      :qty="cartQuantity"
      :stock="productStock"
      :online-disabled="!canAddToCartOnline"
      :pickup-disabled="!canAddToCartPickup"
      :is-pickup="fulfillmentMode === 'pickup'"
      :thumbnail="mainThumbnail"
      :accessory-items="selectedAccessories"
      @blocked="onCtaBlocked"
    />
  </section>
</template>

<script>
import MProductOptionsBundleRz from 'theme/components/molecules/m-product-options-bundle-rz'
import MGuidedCheckoutBar from 'theme/components/molecules/m-guided-checkout-bar'
import MGcStepPackage from 'theme/components/molecules/guided-checkout/m-gc-step-package'
import MGcStepSize from 'theme/components/molecules/guided-checkout/m-gc-step-size'
import MGcStepSkirt from 'theme/components/molecules/guided-checkout/m-gc-step-skirt'
import MGcStepUnderlay from 'theme/components/molecules/guided-checkout/m-gc-step-underlay'
import MGcStepAccessories from 'theme/components/molecules/guided-checkout/m-gc-step-accessories'
import MGcStepFulfillment from 'theme/components/molecules/guided-checkout/m-gc-step-fulfillment'
import {
  estimateProjectArea,
  getAccessoriesTotalAmount,
  getGuidedCheckoutPriceLines,
  getMainBundleOption,
  getUnderlayBundleOption,
  getVisibleSteps,
  getSkirtBundleOption,
  prepareGuidedCheckoutProductForCart
} from 'theme/helpers/guided-checkout'
import { getSelectedBundleOptionValue } from 'theme/helpers/bundle'
import { getThumbnailForProduct } from '@vue-storefront/core/modules/cart/helpers'
import { getProductPrice } from 'theme/helpers'
import { ModalList } from 'theme/store/ui/modals'
import { price } from '@vue-storefront/core/filters/price'
import { optionLabel } from '@vue-storefront/core/modules/catalog/helpers'
import { mapGetters } from 'vuex'
import { onlineHelper } from '@vue-storefront/core/helpers'

export default {
  name: 'OProductGuidedCheckout',
  components: {
    MProductOptionsBundleRz,
    MGuidedCheckoutBar,
    MGcStepPackage,
    MGcStepSize,
    MGcStepSkirt,
    MGcStepUnderlay,
    MGcStepAccessories,
    MGcStepFulfillment
  },
  props: {
    product: {
      type: Object,
      required: true
    },
    productStock: {
      type: Object,
      default: () => ({})
    }
  },
  provide () {
    return {
      prepareGuidedCheckoutForCart: this.prepareForCart
    }
  },
  data () {
    return {
      selectedPreset: 'standard',
      mainQuantity: 1,
      fulfillmentMode: 'delivery',
      selectedAccessories: [],
      summaryTick: 0,
      // $refs are not reactive in Vue 2 — keep a data pointer for child steps
      bundleOptionsComponent: null
    }
  },
  computed: {
    ...mapGetters({
      selectedStore: 'inventorySources/getCurrent'
    }),
    visibleSteps () {
      return getVisibleSteps(this.product, this.selectedPreset)
    },
    underlayOption () {
      return getUnderlayBundleOption(this.product)
    },
    mainOption () {
      return getMainBundleOption(this.product)
    },
    mainOptionValue () {
      return this.mainOption
        ? getSelectedBundleOptionValue(this.product, this.mainOption)
        : null
    },
    selectedSkirtProduct () {
      void this.summaryTick
      const option = getSkirtBundleOption(this.product)
      const selected = option ? getSelectedBundleOptionValue(this.product, option) : null
      return (selected && selected.product) ? selected.product : null
    },
    mainThumbnail () {
      const mainProduct = (this.mainOptionValue && this.mainOptionValue.product) || this.product
      return getThumbnailForProduct(mainProduct)
    },
    // Bundle pack counts live in option_qty; Magento multiplies parent qty × selection qty.
    // Always send parent qty=1 so 95 Pakete stay 95, not 95×95.
    cartQuantity () {
      return 1
    },
    isOnline () {
      return onlineHelper.isOnline
    },
    canAddToCartOnline () {
      if (this.productStock.isLoading) return false
      if (!this.isOnline) return true
      return !!this.productStock.max
    },
    canAddToCartPickup () {
      if (this.productStock.isLoading) return false
      if (!this.selectedStore) return false
      if (this.productStock.maxPickup === null) return false
      if (!this.isOnline) return true
      return !!(this.productStock.maxPickup || this.productStock.max)
    },
    productPrice () {
      return getProductPrice(this.product)
    },
    accessoriesAmount () {
      void this.summaryTick
      return getAccessoriesTotalAmount(this.selectedAccessories)
    },
    numericTotalPrice () {
      void this.summaryTick

      let total = null
      let qty = 1

      for (const option of this.product.bundle_options) {
        const selected = getSelectedBundleOptionValue(this.product, option)
        if (!selected) continue

        if (total === null) {
          total = this.productPrice.special || this.productPrice.regular
          qty = selected.qty
          total *= qty
          continue
        }

        if (!selected.product) continue

        const optionPrice = selected.price
        if (!isNaN(optionPrice)) {
          total += optionPrice * selected.qty
        }
      }

      if (total === null || !this.mainOptionValue || !qty) return null
      return (this.mainOptionValue.qty * (total / qty)) + this.accessoriesAmount
    },
    totalPrice () {
      return this.numericTotalPrice === null ? null : price(this.numericTotalPrice)
    },
    priceLines () {
      void this.summaryTick
      return getGuidedCheckoutPriceLines(this.product, {
        fulfillmentMode: this.fulfillmentMode,
        accessoriesAmount: this.accessoriesAmount,
        accessories: this.selectedAccessories
      })
    },
    summaryAreaLabel () {
      if (!this.mainOptionValue) return ''

      const areaSource = (this.mainOptionValue.product && this.mainOptionValue.product.baseprice_product_amount)
        ? this.mainOptionValue.product
        : this.product
      const area = estimateProjectArea(this.mainOptionValue)
      const unitId = areaSource.baseprice_product_unit
      const unit = unitId != null
        ? (optionLabel(this.$store.state.attribute, {
          attributeKey: 'baseprice_product_unit',
          optionId: unitId
        }) || 'm²')
        : 'm²'
      const packs = this.mainOptionValue.qty || this.mainQuantity || 0

      return `${packs} Paket${packs === 1 ? '' : 'e'} · ca. ${area.toLocaleString('de-DE', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3
      })} ${unit}`
    }
  },
  // Scroll-focus temporarily disabled — keep code for later reactivation.
  // watch: {
  //   visibleSteps () {
  //     // Steps can appear/disappear (skirt/underlay/accessories) — re-collect and re-evaluate.
  //     this.$nextTick(() => {
  //       this.collectStepElements()
  //       this.updateFocusedStep()
  //     })
  //   }
  // },
  // created () {
  //   // Non-reactive helpers for the scroll-focus effect (kept off `data` on purpose).
  //   this.stepElements = []
  //   this.focusFrame = null
  // },
  mounted () {
    document.documentElement.classList.add('has-guided-checkout-bar')
    this.$bus.$on('product-after-bundleoptions', this.refreshSummary)
    this.bindBundleOptionsComponent()
    this.refreshSummary()
    // this.setupStepFocus()
  },
  beforeDestroy () {
    document.documentElement.classList.remove('has-guided-checkout-bar')
    this.$bus.$off('product-after-bundleoptions', this.refreshSummary)
    // this.teardownStepFocus()
  },
  methods: {
    bindBundleOptionsComponent () {
      this.$nextTick(() => {
        this.bundleOptionsComponent = this.$refs.bundleOptions || null
      })
    },
    onCtaBlocked () {
      if (typeof document !== 'undefined') {
        const target = document.getElementById('product-guided-checkout-fulfillment')
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }

      if (this.fulfillmentMode === 'pickup' && !this.selectedStore) {
        this.$store.dispatch('ui/openModal', {
          name: ModalList.StoreSelector,
          payload: this.product
        })
      }
    },
    // --- Scroll focus (disabled) ---
    // setupStepFocus () {
    //   if (typeof window === 'undefined') return
    //
    //   // Respect users who prefer reduced motion — leave every step at full opacity.
    //   const reduceMotion = window.matchMedia &&
    //     window.matchMedia('(prefers-reduced-motion: reduce)').matches
    //   if (reduceMotion) return
    //
    //   this.$nextTick(() => {
    //     this.collectStepElements()
    //     window.addEventListener('scroll', this.onStepFocusScroll, { passive: true })
    //     window.addEventListener('resize', this.onStepFocusScroll, { passive: true })
    //     this.updateFocusedStep()
    //   })
    // },
    // teardownStepFocus () {
    //   if (typeof window === 'undefined') return
    //   window.removeEventListener('scroll', this.onStepFocusScroll)
    //   window.removeEventListener('resize', this.onStepFocusScroll)
    //   if (this.focusFrame) {
    //     cancelAnimationFrame(this.focusFrame)
    //     this.focusFrame = null
    //   }
    // },
    // collectStepElements () {
    //   this.stepElements = this.$el
    //     ? Array.from(this.$el.querySelectorAll('.m-guided-checkout-step'))
    //     : []
    // },
    // onStepFocusScroll () {
    //   // rAF-throttle so scroll stays smooth (only ~6 elements measured per frame).
    //   if (this.focusFrame) return
    //   this.focusFrame = requestAnimationFrame(() => {
    //     this.focusFrame = null
    //     this.updateFocusedStep()
    //   })
    // },
    // updateFocusedStep () {
    //   if (!this.stepElements || !this.stepElements.length) return
    //
    //   const viewportCenter = window.innerHeight / 2
    //   let focused = null
    //   let smallestDistance = Infinity
    //
    //   for (const el of this.stepElements) {
    //     const rect = el.getBoundingClientRect()
    //     const distance = Math.abs((rect.top + rect.height / 2) - viewportCenter)
    //     if (distance < smallestDistance) {
    //       smallestDistance = distance
    //       focused = el
    //     }
    //   }
    //
    //   for (const el of this.stepElements) {
    //     el.classList.toggle('is-focused', el === focused)
    //     el.classList.toggle('is-dimmed', el !== focused)
    //   }
    // },
    isStepVisible (stepId) {
      return this.visibleSteps.some(step => step.id === stepId)
    },
    stepNumber (stepId) {
      const index = this.visibleSteps.findIndex(step => step.id === stepId)
      return index >= 0 ? index + 1 : null
    },
    refreshSummary () {
      this.summaryTick += 1
    },
    async prepareForCart () {
      return prepareGuidedCheckoutProductForCart(this.product, this.$refs.bundleOptions)
    }
  }
}
</script>

<style lang="scss" scoped>
.o-product-guided-checkout {
  margin: 0;
  padding-top: 0;
  border-top: 0;

  &__bundle-engine {
    display: none;
  }

  &__steps {
    display: grid;
    gap: 0;
  }
}

:global(.has-guided-checkout-bar) {
  scroll-padding-bottom: 220px;

  @media (min-width: 1024px) {
    scroll-padding-bottom: 180px;
  }
}
</style>
